import { getAssetShortName } from "./asset/assets";

/**
 * Parse a raw instrument name ("MWETH-USDC", "WNATIVE-MUSDC") into its
 * display-canonical base/quote symbols. The base is normalized through the
 * single source of truth `getAssetShortName` (MWETH→ETH, MWBTC→BTC, WNATIVE→
 * chain native); the mock quote MUSDC→USDC. Returns undefined when the
 * instrument has no "BASE-QUOTE" separator.
 *
 * Shared by display formatting (`getDisplayInstrumentName`) and TradingView
 * symbol resolution (`getTradingViewSymbol`) so both stay consistent.
 */
export const parseInstrument = (
  instrument?: string
): { base: string; quote: string } | undefined => {
  if (!instrument) return undefined;
  const [rawBase, rawQuote] = instrument.split("-");
  if (!rawBase || !rawQuote) return undefined;
  return {
    base: getAssetShortName(rawBase) || rawBase,
    quote: rawQuote === "MUSDC" ? "USDC" : rawQuote,
  };
};

const MARKET_ADDRESS = /^0x[0-9a-fA-F]{40}$/;

/**
 * Whether a name is an on-chain market ADDRESS rather than a symbol. The backend
 * names a position whose market it cannot resolve by the indexer market address
 * (in `instrument_name` and `asset` alike); treating that as a symbol would
 * parse, price or label it as some other market.
 */
export const isMarketAddress = (name?: string | null): name is string =>
  !!name && MARKET_ADDRESS.test(name);

/** "0x1234...abcd", the same shape as `shortenAddress` in utils/strings. */
export const shortMarketAddress = (address: string) =>
  `${address.slice(0, 6)}...${address.slice(-4)}`;

/**
 * Convert an instrument name like "WNATIVE-MUSDC" to a display-friendly
 * name like "MON-USDC" based on chain-specific asset short names. An
 * unresolved market (named by its address) shows as the shortened address.
 */
export const getDisplayInstrumentName = (
  instrumentName?: string
): string | undefined => {
  if (!instrumentName) return undefined;
  if (isMarketAddress(instrumentName)) return shortMarketAddress(instrumentName);
  const parsed = parseInstrument(instrumentName);
  return parsed ? `${parsed.base}-${parsed.quote}` : instrumentName;
};
